/**
 * LiveRoutesMap — Iter-90 · Operations Command Center map view.
 *
 * Renders every in-progress route the caller is authorized to see, driven by
 * `GET /api/operations/live-routes`. Refreshes every 30s. Each route pin
 * shows:
 *   • Crew Leader name + Badge ID
 *   • Current stop N of M
 *   • ETA in minutes
 *   • On-track (gold) vs delayed (red) coloring
 * Each stop marker is color-coded — completed (green), pending (grey),
 * skipped (red).
 *
 * Region scoping is enforced server-side; the frontend simply renders what
 * the endpoint returns.
 *
 * Constraint: Does NOT touch route/crew assignment logic — it's a pure
 * read-only visualization sitting inside the Operations department.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { MapPin, RefreshCw, Truck, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { MapContainer, TileLayer, CircleMarker, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

const API = process.env.REACT_APP_BACKEND_URL;
const REFRESH_MS = 30000;
const DEFAULT_CENTER = [32.7815, -96.7970]; // Dallas anchor
const DEFAULT_ZOOM = 11;

const STOP_COLOR = {
  completed: '#10b981', // green
  pending:   '#71717a', // grey
  skipped:   '#ef4444', // red
};

const crewIcon = (delayed) => L.divIcon({
  className: 'live-crew-marker',
  html: `<div style="position:relative;">
           <div style="position:absolute;inset:-6px;border-radius:9999px;background:${delayed ? '#ef4444' : '#FFD60A'};opacity:.35;animation:necs-pulse 1.6s ease-out infinite;"></div>
           <div style="position:relative;width:32px;height:32px;border-radius:9999px;background:${delayed ? '#ef4444' : '#FFD60A'};border:2px solid #0a0a0a;display:flex;align-items:center;justify-content:center;font-size:16px;">🚛</div>
         </div>
         <style>@keyframes necs-pulse{0%{transform:scale(1);opacity:.6}100%{transform:scale(2.2);opacity:0}}</style>`,
  iconSize: [32, 32],
  iconAnchor: [16, 16],
});

export default function LiveRoutesMap() {
  const [routes, setRoutes] = useState([]);
  const [counts, setCounts] = useState({ total: 0, on_track: 0, delayed: 0 });
  const [byRegion, setByRegion] = useState([]);
  const [scope, setScope] = useState('own_region');
  const [lastUpdate, setLastUpdate] = useState(null);
  const [loading, setLoading] = useState(false);
  const timerRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/operations/live-routes`, { credentials: 'include' });
      if (!res.ok) return;
      const data = await res.json();
      setRoutes(data.routes || []);
      setCounts(data.counts || { total: 0, on_track: 0, delayed: 0 });
      setByRegion(data.by_region || []);
      setScope(data.scope || 'own_region');
      setLastUpdate(new Date());
    } catch {
      /* silent — next 30s tick will retry */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    timerRef.current = setInterval(load, REFRESH_MS);
    return () => clearInterval(timerRef.current);
  }, []);

  const center = useMemo(() => {
    const first = routes.find((r) => r.location);
    return first ? [first.location.latitude, first.location.longitude] : DEFAULT_CENTER;
  }, [routes]);

  return (
    <Card className="bg-zinc-900 border border-zinc-800 p-4" data-testid="ops-live-routes-map">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div className="flex items-center gap-2">
          <MapPin className="h-4 w-4 text-[#C9A962]" />
          <h3 className="text-white font-black text-base" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            LIVE ROUTES
          </h3>
          <Badge className="bg-zinc-800 text-[10px] uppercase tracking-widest">
            {scope === 'all_regions' ? 'Company-wide' : 'Your region'}
          </Badge>
        </div>
        <div className="flex items-center gap-3 text-[11px]">
          <span className="flex items-center gap-1 text-emerald-400" data-testid="ops-live-routes-on-track">
            <CheckCircle2 className="h-3 w-3" /> On-track: <b>{counts.on_track}</b>
          </span>
          <span className="flex items-center gap-1 text-red-400" data-testid="ops-live-routes-delayed">
            <AlertTriangle className="h-3 w-3" /> Delayed: <b>{counts.delayed}</b>
          </span>
          <span className="flex items-center gap-1 text-zinc-400">
            <Truck className="h-3 w-3" /> Total: <b>{counts.total}</b>
          </span>
          <button
            type="button"
            onClick={load}
            disabled={loading}
            className="ml-2 text-zinc-500 hover:text-white transition"
            data-testid="ops-live-routes-refresh"
            title="Refresh"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {routes.length === 0 ? (
        <div className="h-72 flex items-center justify-center border border-dashed border-zinc-700 rounded-lg text-zinc-500 text-sm" data-testid="ops-live-routes-empty">
          No active routes right now.
        </div>
      ) : (
        <>
          {/* Iter-90 · Director-tier region rollup — shows total / on-track /
              delayed per region above the map. Only meaningful when the
              caller sees more than one region; single-region viewers hide
              this to save space. */}
          {byRegion.length > 1 && (
            <div className="mb-3 grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="ops-live-routes-by-region">
              {byRegion.map((rg) => (
                <div
                  key={rg.region_id}
                  className="p-2 rounded border border-zinc-800 bg-zinc-950"
                  data-testid={`ops-live-routes-region-${rg.region_id}`}
                >
                  <p className="text-[10px] uppercase tracking-widest text-[#C9A962] font-bold truncate">{rg.region_name}</p>
                  <p className="text-xl font-black text-white leading-none mt-1">{rg.total}</p>
                  <p className="text-[10px] text-zinc-400 mt-1">
                    <span className="text-emerald-400 font-semibold">{rg.on_track} on-track</span>
                    <span className="mx-1">·</span>
                    <span className="text-red-400 font-semibold">{rg.delayed} delayed</span>
                  </p>
                  {rg.cities?.length > 0 && (
                    <p className="text-[9px] text-zinc-500 truncate mt-1" title={rg.cities.join(', ')}>
                      {rg.cities.join(' · ')}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
          <div className="rounded-lg overflow-hidden border border-zinc-800" style={{ height: 400 }}>
            <MapContainer center={center} zoom={DEFAULT_ZOOM} style={{ height: '100%', width: '100%' }} scrollWheelZoom>
              <TileLayer
                url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
                attribution='&copy; OpenStreetMap · &copy; CARTO'
              />
              {routes.map((r) => (
                <RouteLayer key={r.route_id} route={r} />
              ))}
            </MapContainer>
          </div>
        </>
      )}

      <div className="mt-2 flex items-center justify-between text-[10px] text-zinc-500 uppercase tracking-widest">
        <div className="flex items-center gap-3">
          <LegendDot color={STOP_COLOR.completed} label="Completed" />
          <LegendDot color={STOP_COLOR.pending}   label="Pending" />
          <LegendDot color={STOP_COLOR.skipped}   label="Skipped" />
        </div>
        <span>Auto-refresh · 30s{lastUpdate ? ` · ${lastUpdate.toLocaleTimeString()}` : ''}</span>
      </div>
    </Card>
  );
}

function RouteLayer({ route }) {
  const stops = route.location_history || [];
  const skipped = new Set(route.skipped_units || []);
  const pos = route.location;
  return (
    <>
      {stops.map((s, idx) => (
        <CircleMarker
          key={`${route.route_id}-stop-${idx}`}
          center={[s.lat, s.lng]}
          radius={5}
          pathOptions={{
            color: skipped.has(s.unit) ? STOP_COLOR.skipped : STOP_COLOR.completed,
            fillColor: skipped.has(s.unit) ? STOP_COLOR.skipped : STOP_COLOR.completed,
            fillOpacity: 0.9,
          }}
        >
          <Popup>
            <div style={{ fontSize: 12 }}>
              <b>Unit {s.unit || '—'}</b><br />
              {skipped.has(s.unit) ? 'Skipped · violation logged' : 'Completed'}
            </div>
          </Popup>
        </CircleMarker>
      ))}
      {pos && (
        <Marker position={[pos.latitude, pos.longitude]} icon={crewIcon(route.delayed)}>
          <Popup>
            <div style={{ fontSize: 12, minWidth: 200 }} data-testid={`ops-live-route-popup-${route.route_id}`}>
              <b>{route.crew_leader_name}</b><br />
              Badge <span style={{ fontFamily: 'monospace' }}>{route.badge_number}</span><br />
              <span style={{ color: '#0f766e' }}>{route.property_name}</span><br />
              Stop <b>{route.current_stop}</b> of <b>{route.total_stops}</b>
              <span style={{ marginLeft: 6, padding: '1px 6px', borderRadius: 999, fontSize: 10, background: route.delayed ? '#fee2e2' : '#dcfce7', color: route.delayed ? '#991b1b' : '#166534' }}>
                {route.delayed ? 'DELAYED' : 'ON-TRACK'}
              </span><br />
              ETA <b>~{route.eta_minutes} min</b>
            </div>
          </Popup>
        </Marker>
      )}
    </>
  );
}

function LegendDot({ color, label }) {
  return (
    <span className="flex items-center gap-1">
      <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: color }} />
      {label}
    </span>
  );
}
